import { Translate } from 'react-redux-i18n'
import { FORMAT, formatDateTime } from '../../../helpers/datetime'
import { formatVal } from '../../../helpers/value'
import { COMPANY_TYPES, EMPTY_VALUE } from '../../../utils/common'
import { PANELS } from '../constant'
import { LastUpdateDate } from './LastUpdateDate'
import { Price } from './Price'
import { Score } from './Score'

const HIGHLIGHT_KEYS = {
  LAST_UPDATE_DATE: 'LAST_UPDATE_DATE',
  CREDIT_RISK_SCORE: 'CREDIT_RISK_SCORE',
  PERFORMANCE_SCORE: 'PERFORMANCE_SCORE',
  YEAR_OF_OPERATIONS: 'YEAR_OF_OPERATIONS',
  CHARTER_CAPITAL: 'CHARTER_CAPITAL',
  FISCAL_YEAR_END: 'FISCAL_YEAR_END',
  TOTAL_ASSETS: 'TOTAL_ASSETS',
  BRANCHS: 'BRANCHS',
  SUBSIDIARIES: 'SUBSIDIARIES',
  TOTAL_OPERATING_INCOME: 'TOTAL_OPERATING_INCOME',
}

export const getHighlightData = ({ rawData, comType, locale, timezone }) => {
  const highlightKeys = getHighlighKeys(comType)

  return highlightKeys.map((highlightKey) => {
    const title = getTitle({ highlightKey })
    const detail = getDetail({ rawData, highlightKey, locale, timezone })
    const isShowSeparator = getIsSeparate({ comType, highlightKey })
    const scrollElementClass = getScrollElementClass({ highlightKey })

    return { title, detail, isShowSeparator, scrollElementClass }
  })
}

const getHighlighKeys = (comType) => {
  switch (comType) {
    case COMPANY_TYPES.CORPORATE:
      return [
        HIGHLIGHT_KEYS.LAST_UPDATE_DATE,
        HIGHLIGHT_KEYS.CREDIT_RISK_SCORE,
        HIGHLIGHT_KEYS.PERFORMANCE_SCORE,
        HIGHLIGHT_KEYS.YEAR_OF_OPERATIONS,
        HIGHLIGHT_KEYS.CHARTER_CAPITAL,
        HIGHLIGHT_KEYS.FISCAL_YEAR_END,
      ]
    case COMPANY_TYPES.BANK:
      return [
        HIGHLIGHT_KEYS.BRANCHS,
        HIGHLIGHT_KEYS.SUBSIDIARIES,
        HIGHLIGHT_KEYS.TOTAL_ASSETS,
        HIGHLIGHT_KEYS.YEAR_OF_OPERATIONS,
        HIGHLIGHT_KEYS.CHARTER_CAPITAL,
        HIGHLIGHT_KEYS.FISCAL_YEAR_END,
      ]
    case COMPANY_TYPES.SECURITIES:
      return [
        HIGHLIGHT_KEYS.TOTAL_OPERATING_INCOME,
        HIGHLIGHT_KEYS.TOTAL_ASSETS,
        HIGHLIGHT_KEYS.YEAR_OF_OPERATIONS,
        HIGHLIGHT_KEYS.CHARTER_CAPITAL,
        HIGHLIGHT_KEYS.FISCAL_YEAR_END,
      ]
    default:
      return []
  }
}

const getTitle = ({ highlightKey }) => {
  const title = 'companySummary.highlight.' + highlightKey
  return <Translate value={title} />
}

const getDetail = ({ rawData, highlightKey, locale, timezone }) => {
  const year = new Date(rawData.fiscalYearEnd).getFullYear()
  switch (highlightKey) {
    case HIGHLIGHT_KEYS.LAST_UPDATE_DATE:
      return (
        <LastUpdateDate rawData={rawData} locale={locale} timezone={timezone} />
      )
    case HIGHLIGHT_KEYS.CREDIT_RISK_SCORE:
      return (
        <Score
          score={rawData.score?.creditRiskScore}
          prevScore={rawData.score?.prevCreditRiskScore}
          colorScore={rawData.score?.creditRiskScoreColor}
        />
      )
    case HIGHLIGHT_KEYS.PERFORMANCE_SCORE:
      return (
        <Score
          score={rawData.score?.performanceScore}
          prevScore={rawData.score?.prevPerformanceScore}
          colorScore={rawData.score?.performanceScoreColor}
        />
      )
    case HIGHLIGHT_KEYS.YEAR_OF_OPERATIONS:
      if (!rawData.age && rawData.age !== 0) return EMPTY_VALUE
      return (
        <div className="textLowercase">
          {rawData.age} <Translate value="companySummary.YEARS" />
        </div>
      )
    case HIGHLIGHT_KEYS.CHARTER_CAPITAL:
      return (
        <Price
          value={rawData.charterCapital}
          year={year}
          exchangeRateType="lastDayRate"
        />
      )
    case HIGHLIGHT_KEYS.FISCAL_YEAR_END:
      return formatDateTime(
        rawData.fiscalYearEnd,
        FORMAT.DAY_MONTH,
        locale,
        timezone,
      )
    case HIGHLIGHT_KEYS.TOTAL_ASSETS:
      return (
        <Price
          value={rawData.totalAssets}
          year={year}
          exchangeRateType="lastDayRate"
        />
      )
    case HIGHLIGHT_KEYS.BRANCHS:
      return formatVal(rawData.totalBranch, locale, false, 0)
    case HIGHLIGHT_KEYS.SUBSIDIARIES:
      return formatVal(rawData.totalSubsidiaries, locale, false, 0)
    case HIGHLIGHT_KEYS.TOTAL_OPERATING_INCOME:
      return (
        <Price
          value={rawData.revenue}
          year={year}
          exchangeRateType="averageRate"
        />
      )
    default:
      return ''
  }
}

const getIsSeparate = ({ comType, highlightKey }) => {
  switch (comType) {
    case COMPANY_TYPES.CORPORATE:
      return [HIGHLIGHT_KEYS.PERFORMANCE_SCORE].includes(highlightKey)
    case COMPANY_TYPES.BANK:
      return [
        HIGHLIGHT_KEYS.SUBSIDIARIES,
        HIGHLIGHT_KEYS.TOTAL_ASSETS,
      ].includes(highlightKey)
    case COMPANY_TYPES.SECURITIES:
      return [HIGHLIGHT_KEYS.TOTAL_ASSETS].includes(highlightKey)
    default:
      return false
  }
}

const getScrollElementClass = ({ highlightKey }) => {
  switch (highlightKey) {
    case HIGHLIGHT_KEYS.LAST_UPDATE_DATE:
    case HIGHLIGHT_KEYS.CREDIT_RISK_SCORE:
    case HIGHLIGHT_KEYS.PERFORMANCE_SCORE:
      return PANELS.SCORE
    case HIGHLIGHT_KEYS.YEAR_OF_OPERATIONS:
    case HIGHLIGHT_KEYS.CHARTER_CAPITAL:
    case HIGHLIGHT_KEYS.FISCAL_YEAR_END:
      return PANELS.COMPANY_OVERVIEW
    case HIGHLIGHT_KEYS.TOTAL_ASSETS:
      return PANELS.KEY_FINANCIALS
    case HIGHLIGHT_KEYS.BRANCHS:
    case HIGHLIGHT_KEYS.SUBSIDIARIES:
      return PANELS.OWNERSHIP_AND_GROUP
    case HIGHLIGHT_KEYS.TOTAL_OPERATING_INCOME:
      return PANELS.KEY_FINANCIALS
    default:
      return ''
  }
}
